import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import TrendingDownOutlinedIcon from "@mui/icons-material/TrendingDownOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import { Alert, Button, Skeleton } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "../components/ui/chart";
import { TipsCarousel } from "../features/tips/TipsCarousel";
import { financeApi } from "../lib/api";
import { userAvatarColor, userInitials } from "../lib/avatar";
import { useDevDateOverride } from "../lib/devDate";
import { CategoryIcon, currencySymbol, money } from "../lib/format";
import { queryKeys } from "../lib/queryKeys";
import type { Group, Overview, User } from "../lib/types";

type OverviewPageProps = {
  group: Group;
  user: User;
};

const spendingConfig = {
  amount: { label: "Expense", color: "#2563eb" },
} satisfies ChartConfig;

function formatCurrency(value: string | number, currency: string) {
  return `${currencySymbol(currency)}${money(value)}`;
}

function formatDate(value: string, options: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" }) {
  const date = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, options);
}

function firstName(value: string) {
  return value.trim().split(/\s+/)[0] || "there";
}

function MetricCard({
  label,
  value,
  subtitle,
  icon,
  tone,
}: {
  label: string;
  value: string;
  subtitle: string;
  icon: ReactNode;
  tone?: "income" | "expense";
}) {
  return (
    <article className={`overview-metric${tone ? ` ${tone}` : ""}`}>
      <span className="overview-metric-icon">{icon}</span>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{subtitle}</small>
    </article>
  );
}

function EmptyOverviewState({ title, text, to, label }: { title: string; text: string; to: string; label: string }) {
  return (
    <div className="overview-empty-state">
      <strong>{title}</strong>
      <span>{text}</span>
      <Button className="overview-secondary-action" component={Link} to={to}>
        {label}
      </Button>
    </div>
  );
}

function SpendingByDay({ overview }: { overview: Overview }) {
  const chartData = useMemo(
    () =>
      overview.daily_expenses.map((item) => ({
        date: item.date,
        label: formatDate(item.date, { day: "numeric", month: "short" }),
        amount: Number(item.amount),
      })),
    [overview.daily_expenses],
  );
  const dateLabels = useMemo(
    () =>
      Object.fromEntries(
        chartData.map((item) => [item.date, item.label]),
      ) as Record<string, string>,
    [chartData],
  );
  const values = chartData.map((item) => item.amount);
  const max = Math.max(...values, 0);
  const peakIndex = values.findIndex((value) => value === max && value > 0);

  return (
    <section className="overview-section overview-spending-section">
      <div className="overview-section-heading">
        <div>
          <h2>Spending by day</h2>
          <p>Last 14 days</p>
        </div>
        <Button component={Link} to="/statistics" className="overview-link-button">
          Statistics
        </Button>
      </div>
      {max <= 0 ? (
        <EmptyOverviewState title="No recent expenses" text="Add a record or import a statement to see daily spending." to="/records" label="Open records" />
      ) : (
        <ChartContainer config={spendingConfig} className="overview-day-chart">
          <BarChart accessibilityLayer data={chartData} margin={{ left: 8, right: 8, top: 12, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="rgba(15, 23, 42, 0.08)" />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              tick={{ fontSize: 12 }}
              interval="preserveStartEnd"
              minTickGap={24}
              tickFormatter={(value) => dateLabels[String(value)] ?? String(value)}
            />
            <YAxis hide domain={[0, Math.ceil(max)]} />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={(value) => dateLabels[String(value)] ?? String(value)}
                  valueFormatter={(value) => formatCurrency(value, overview.base_currency)}
                />
              }
            />
            <Bar dataKey="amount" radius={5} maxBarSize={34} fill="var(--color-amount)">
              {chartData.map((entry, index) => (
                <Cell key={entry.date} fill={index === peakIndex ? "var(--color-amount)" : "#93a4b8"} />
              ))}
            </Bar>
          </BarChart>
        </ChartContainer>
      )}
    </section>
  );
}

function CategoriesAndPayments({ overview }: { overview: Overview }) {
  const maxCategory = Math.max(...overview.top_categories.map((item) => Number(item.amount)), 0);

  return (
    <section className="overview-two-column">
      <div className="overview-section">
        <div className="overview-section-heading">
          <div>
            <h2>Top categories</h2>
            <p>This month</p>
          </div>
          <CategoryOutlinedIcon aria-hidden="true" />
        </div>
        {overview.top_categories.length ? (
          <div className="overview-category-list">
            {overview.top_categories.map((category) => (
              <div key={category.category_id || category.name} className="overview-category-row">
                <CategoryIcon icon={category.icon} color={category.color} />
                <span>
                  <strong>{category.name}</strong>
                  <i>
                    <b style={{ width: `${maxCategory ? (Number(category.amount) / maxCategory) * 100 : 0}%` }} />
                  </i>
                </span>
                <em>{formatCurrency(category.amount, overview.base_currency)}</em>
              </div>
            ))}
          </div>
        ) : (
          <EmptyOverviewState title="No category spend" text="Categorized expenses will appear here automatically." to="/records?create=expense" label="Add record" />
        )}
      </div>

      <div className="overview-section">
        <div className="overview-section-heading">
          <div>
            <h2>Upcoming payments</h2>
            <p>Total until month end: {formatCurrency(overview.upcoming_total, overview.base_currency)}</p>
          </div>
          <CalendarMonthOutlinedIcon aria-hidden="true" />
        </div>
        {overview.upcoming_payments.length ? (
          <div className="overview-payment-list">
            {overview.upcoming_payments.map((payment) => (
              <Link key={payment.id} className="overview-payment-row" to="/recurring-payments">
                <span style={{ backgroundColor: `${payment.category_color || "#2563eb"}22` }}>
                  <PaymentsOutlinedIcon aria-hidden="true" />
                </span>
                <span>
                  <strong>{payment.name}</strong>
                  <small>{formatDate(payment.due_date)}{payment.category_name ? ` · ${payment.category_name}` : ""}</small>
                </span>
                <em>{formatCurrency(payment.amount, payment.currency)}</em>
              </Link>
            ))}
          </div>
        ) : (
          <EmptyOverviewState title="No payments due" text="Recurring payments due this month will show up here." to="/recurring-payments" label="Set up payment" />
        )}
      </div>
    </section>
  );
}

function RecentRecords({ overview }: { overview: Overview }) {
  return (
    <section className="overview-section">
      <div className="overview-section-heading">
        <div>
          <h2>Recent records</h2>
          <p>Latest family activity</p>
        </div>
        <Button component={Link} to="/records" className="overview-link-button">
          Records
        </Button>
      </div>
      {overview.recent_records.length ? (
        <div className="overview-record-list">
          {overview.recent_records.map((record) => (
            <Link key={record.id} className="overview-record-row" to="/records">
              <span
                className={`overview-record-avatar${record.user_avatar_data_url ? " has-image" : ""}`}
                style={{
                  backgroundColor: record.user_avatar_data_url
                    ? undefined
                    : userAvatarColor({ id: record.user_id, email: record.user_display_name, display_name: record.user_display_name }),
                }}
              >
                {record.user_avatar_data_url ? (
                  <img src={record.user_avatar_data_url} alt="" />
                ) : (
                  userInitials({ id: record.user_id, email: record.user_display_name, display_name: record.user_display_name })
                )}
              </span>
              <CategoryIcon icon={record.category_icon} color={record.category_color} />
              <span>
                <strong>{record.merchant || record.note || record.category_name || "Record"}</strong>
                <small>{record.user_display_name} · {record.account_name} · {formatDate(record.occurred_at)}</small>
              </span>
              <em className={record.type}>{record.type === "expense" ? "-" : record.type === "income" ? "+" : ""}{formatCurrency(record.amount, record.currency)}</em>
            </Link>
          ))}
        </div>
      ) : (
        <EmptyOverviewState title="No records yet" text="Your latest records and import results will appear here." to="/records?create=expense" label="Create first record" />
      )}
    </section>
  );
}

function OverviewSkeleton() {
  return (
    <section className="content overview-content">
      <div className="overview-metric-grid">
        {[0, 1, 2].map((item) => <Skeleton key={item} variant="rounded" height={136} />)}
      </div>
      <Skeleton variant="rounded" height={170} />
      <Skeleton variant="rounded" height={240} />
    </section>
  );
}

export function OverviewPage({ group, user }: OverviewPageProps) {
  const asOfDate = useDevDateOverride();
  const overviewQuery = useQuery({
    queryKey: [...queryKeys.overview(group.id), asOfDate],
    queryFn: () => financeApi.overview(group.id),
  });
  const overview = overviewQuery.data;

  if (overviewQuery.isLoading) return <OverviewSkeleton />;

  if (overviewQuery.isError || !overview) {
    return (
      <section className="content overview-content">
        <Alert severity="error">Could not load overview. Try refreshing the page.</Alert>
      </section>
    );
  }

  return (
    <>
      <header className="topbar overview-topbar">
        <div>
          <h1>Hi, {firstName(user.display_name)}</h1>
          <p>{overview.family_name || group.name} · {overview.month}</p>
        </div>
        <div className="overview-actions">
          <Button className="overview-secondary-action" component={Link} to="/records?import=statement">
            <FileUploadOutlinedIcon aria-hidden="true" />
            Import statement
          </Button>
          <Button className="overview-primary-action" component={Link} to="/records?create=expense">
            <AddOutlinedIcon aria-hidden="true" />
            Add record
          </Button>
        </div>
      </header>

      <section className="content overview-content">
        <TipsCarousel />
        <div className="overview-metric-grid">
          <MetricCard
            label="Total balance"
            value={formatCurrency(overview.total_balance.amount, overview.total_balance.currency)}
            subtitle={`${overview.total_balance.account_count} accounts · ${overview.total_balance.currency_count} currencies`}
            icon={<AccountBalanceWalletOutlinedIcon aria-hidden="true" />}
          />
          <MetricCard
            label="Expenses this month"
            value={formatCurrency(overview.expenses.current, overview.expenses.currency)}
            subtitle={overview.expenses.note}
            icon={<TrendingDownOutlinedIcon aria-hidden="true" />}
            tone="expense"
          />
          <MetricCard
            label="Income this month"
            value={formatCurrency(overview.income.current, overview.income.currency)}
            subtitle={overview.income.note}
            icon={<TrendingUpOutlinedIcon aria-hidden="true" />}
            tone="income"
          />
        </div>
        <SpendingByDay overview={overview} />
        <CategoriesAndPayments overview={overview} />
        <RecentRecords overview={overview} />
      </section>
    </>
  );
}
